import { currentLocale } from '@/lib/locale';
import { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { trpc } from '@/lib/trpc';
import { useTranslation } from 'react-i18next';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LineChart,
  Line,
  ResponsiveContainer,
} from 'recharts';
import { Monitor, CheckCircle2, Archive, Wrench } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getStatusHex } from './DeviceStatusBadge';
import { DeliveredCrossCheckCard } from './DeliveredCrossCheckCard';
import { isReadyToDeliver } from '../../../../shared/inventory-status';

type MetricsView = 'active' | 'intermediate' | 'inactive' | 'all';
const VIEWS: MetricsView[] = ['active', 'intermediate', 'inactive', 'all'];

/** Colores de las series del gráfico de entradas al taller (mismos hex que los badges). */
const INCIDENT_SERIES = [
  { key: 'reparar', status: 'Pending: Para Reparar' },
  { key: 'revisar', status: 'Pending: Revisar' },
  { key: 'formateo', status: 'Pending: Formateo' },
  { key: 'localizar', status: 'Localizar' },
  { key: 'recupero', status: 'En proceso de recupero' },
] as const;

// Paleta categórica estable por NOMBRE (no por índice) para categorías/clapbases
const CATEGORICAL = ['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316', '#84cc16'];
function stableColor(name: string): string {
  let hash = 0;
  for (const c of name) hash = (hash * 31 + c.charCodeAt(0)) & 0xffffff;
  return CATEGORICAL[Math.abs(hash) % CATEGORICAL.length];
}

function formatMonth(month: string): string {
  const [year, m] = month.split('-');
  const date = new Date(Number(year), Number(m) - 1, 1);
  return date.toLocaleDateString(currentLocale(), { month: 'short', year: '2-digit' });
}

export function InventoryMetrics() {
  const { t } = useTranslation();
  const { data: metrics, isLoading } = trpc.inventory.getInventoryMetrics.useQuery();
  const [view, setView] = useState<MetricsView>('active');

  // Vista discriminada: derivada del response particionado (sin queries extra).
  // "Lista para Entregar" se abre en dos porciones: asignadas / sin asignar.
  const derived = useMemo(() => {
    if (!metrics) return null;
    const inView = metrics.byStatus.filter((s) => (view === 'all' ? true : s.bucket === view));
    const byStatus: { key: string; label: string; count: number; color: string }[] = [];
    // El desglose asignada/sin asignar es GLOBAL (normalizado): si la tabla
    // trajera dos variantes de casing de "Lista para Entregar", entra una sola vez.
    let readySplitDone = false;
    for (const s of inView) {
      if (isReadyToDeliver(s.status)) {
        if (readySplitDone) continue;
        readySplitDone = true;
        const { assigned, unassigned } = metrics.readyToDeliver;
        if (assigned > 0) byStatus.push({ key: `${s.status}|assigned`, label: `${s.status} · ${t('devices.split.ready_assigned')}`, count: assigned, color: getStatusHex(s.status) });
        if (unassigned > 0) byStatus.push({ key: `${s.status}|unassigned`, label: `${s.status} · ${t('devices.split.ready_unassigned')}`, count: unassigned, color: getStatusHex(s.status, 'unassigned') });
      } else {
        byStatus.push({ key: s.status, label: s.status, count: s.count, color: getStatusHex(s.status) });
      }
    }
    const pick = (row: { count: number; activeCount: number; intermediateCount: number; inactiveCount: number }) =>
      view === 'all' ? row.count
      : view === 'active' ? row.activeCount
      : view === 'intermediate' ? row.intermediateCount
      : row.inactiveCount;
    const byCategory = metrics.byCategory
      .map((c) => ({ ...c, value: pick(c) }))
      .filter((c) => c.value > 0);
    const byClapBase = metrics.byClapBase
      .map((c) => ({ ...c, value: pick(c) }))
      .filter((c) => c.value > 0);
    const viewTotal =
      view === 'all' ? metrics.total
      : view === 'active' ? metrics.activos
      : view === 'intermediate' ? metrics.intermedios
      : metrics.inactivos;
    const incidents = metrics.incidentsByMonth.map((m) => ({ ...m, label: formatMonth(m.month) }));
    return { byStatus, byCategory, byClapBase, viewTotal, incidents };
  }, [metrics, view, t]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48 text-muted-foreground">
        {t('devices.metrics.loading')}
      </div>
    );
  }

  if (!metrics || !derived) return null;

  const kpis: { key: MetricsView; label: string; value: number; hint?: string; icon: typeof Monitor; tone: string }[] = [
    { key: 'all', label: t('devices.metrics.total_devices'), value: metrics.total, icon: Monitor, tone: 'text-indigo-500' },
    {
      key: 'active',
      label: t('devices.metrics.active_devices'),
      value: metrics.activos,
      hint: t('devices.metrics.ready_split_hint', { assigned: metrics.readyToDeliver.assigned, unassigned: metrics.readyToDeliver.unassigned }),
      icon: CheckCircle2,
      tone: 'text-green-500',
    },
    { key: 'intermediate', label: t('devices.metrics.intermediate_devices'), value: metrics.intermedios, hint: t('devices.metrics.intermediate_hint'), icon: Wrench, tone: 'text-amber-500' },
    { key: 'inactive', label: t('devices.metrics.inactive_devices'), value: metrics.inactivos, icon: Archive, tone: 'text-red-500' },
  ];

  const viewLabel = t(`devices.metrics.view_${view}`);

  return (
    <div className="space-y-6">
      {/* KPIs Total / Activos / Intermedios / Inactivos — clickeables, setean la vista */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map(({ key, label, value, hint, icon: Icon, tone }) => (
          <Card
            key={key}
            className={cn('cursor-pointer transition-all hover:shadow-md', view === key && 'ring-2 ring-primary ring-offset-1')}
            onClick={() => setView(key)}
          >
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <Icon className={cn('h-8 w-8 shrink-0', tone)} />
                <div className="min-w-0">
                  <p className="text-sm text-muted-foreground">{label}</p>
                  <p className="text-3xl font-bold tabular-nums">{value}</p>
                  {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Selector de vista (espejo de las cards, accesible sin scroll) */}
      <div className="flex flex-wrap items-center gap-2">
        {VIEWS.map((key) => (
          <Button
            key={key}
            size="sm"
            variant={view === key ? 'default' : 'outline'}
            className="h-8"
            onClick={() => setView(key)}
          >
            {t(`devices.metrics.view_${key}`)}
          </Button>
        ))}
      </div>

      {/* Entregados vs community map (siempre TOTAL) */}
      <DeliveredCrossCheckCard />

      {/* Pie por estado + Bar por categoría (de la vista) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {t('devices.metrics.by_status')}
              <span className="ml-2 text-sm font-normal text-muted-foreground">({viewLabel})</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center gap-4">
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={derived.byStatus}
                    dataKey="count"
                    nameKey="label"
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={90}
                  >
                    {derived.byStatus.map((item) => (
                      <Cell key={item.key} fill={item.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value, _name, props) => [
                      `${value} ${t('devices.metrics.devices')} (${((props.payload.count / Math.max(derived.viewTotal, 1)) * 100).toFixed(0)}%)`,
                      props.payload.label,
                    ]}
                  />
                </PieChart>
              </ResponsiveContainer>
              {/* Leyenda con los MISMOS colores que los badges de la tabla */}
              <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
                {derived.byStatus.map((item) => (
                  <div key={item.key} className="flex items-center gap-2 min-w-0">
                    <div
                      className="h-2.5 w-2.5 rounded-full shrink-0"
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="text-sm truncate text-muted-foreground flex-1" title={item.label}>
                      {item.label}
                    </span>
                    <span className="text-sm font-semibold tabular-nums shrink-0">{item.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {t('devices.metrics.by_category')}
              <span className="ml-2 text-sm font-normal text-muted-foreground">({viewLabel})</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={derived.byCategory} layout="vertical" margin={{ left: 16 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" />
                <YAxis type="category" dataKey="category" width={130} tick={{ fontSize: 12 }} />
                <Tooltip formatter={(value) => [value, t('devices.metrics.devices')]} />
                <Bar dataKey="value" fill="#6366f1" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Entradas al taller por mes (desde el historial de cambios de estado):
          cuántas compus se mandan a reparar / revisar / formateo / localizar /
          recupero. Siempre TOTAL: es un flujo, no un stock (Sol 15/09). */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t('devices.metrics.incidents_title')}
            <span className="ml-2 text-sm font-normal text-muted-foreground">
              ({t('devices.metrics.last_12_months')} · {t('devices.metrics.incidents_note')})
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={derived.incidents}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={false} />
              <Tooltip
                formatter={(value, name) => [value, INCIDENT_SERIES.find((s) => s.key === name)?.status ?? String(name)]}
                labelFormatter={(label, payload) => {
                  const total = payload?.[0]?.payload?.total ?? 0;
                  return `${label} · ${t('devices.metrics.incidents_total', { count: total })}`;
                }}
              />
              {INCIDENT_SERIES.map((serie) => (
                <Bar key={serie.key} dataKey={serie.key} stackId="taller" fill={getStatusHex(serie.status)} radius={0} />
              ))}
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5">
            {INCIDENT_SERIES.map((serie) => (
              <div key={serie.key} className="flex items-center gap-2">
                <div className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: getStatusHex(serie.status) }} />
                <span className="text-sm text-muted-foreground">{serie.status}</span>
                <span className="text-sm font-semibold tabular-nums">
                  {derived.incidents.reduce((acc, m) => acc + m[serie.key], 0)}
                </span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Tendencia mensual (siempre TOTAL: son altas al inventario) */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t('devices.metrics.monthly_trend')}
            <span className="ml-2 text-sm font-normal text-muted-foreground">
              ({t('devices.metrics.last_12_months')} · {t('devices.metrics.all_purchases_note')})
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={metrics.byMonth.map((d) => ({ ...d, label: formatMonth(d.month) }))}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={false} />
              <Tooltip formatter={(value) => [value, t('devices.metrics.devices')]} />
              <Line
                type="monotone"
                dataKey="count"
                stroke="#6366f1"
                strokeWidth={2}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Por ClapBase (de la vista) */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t('devices.metrics.by_clapbase')}
            <span className="ml-2 text-sm font-normal text-muted-foreground">({viewLabel})</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={derived.byClapBase}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="clapBase" tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={false} />
              <Tooltip formatter={(value) => [value, t('devices.metrics.devices')]} />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {derived.byClapBase.map((item) => (
                  <Cell key={item.clapBase} fill={stableColor(item.clapBase)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
}
